import { Component, OnInit } from '@angular/core';
import CreditsManagersModel, { SubscriberPolicyDetails } from '../services/creditsManagers.model';
import { ToastrService } from 'ngx-toastr';
import { CreditsManagersService } from '../services/creditsManagers.service';

declare var $: any;

/**
 * @component SubscriptionPlansComponent
 * @description Manages and displays subscription plans for a business.
 * Handles fetching subscriptions from the backend, extracting unique policies,
 * filtering subscriptions by selected policy tabs, and displaying device details
 * in a modal popup. Provides complete subscription management UI with loading
 * states and error handling through toast notifications.
 * 
 * Responsibilities:
 * - Fetch active subscriptions for the logged-in business from the backend
 * - Extract and display unique policy details from subscriptions
 * - Filter subscriptions based on selected policy tab
 * - Display detailed device information for selected subscriptions
 * - Manage loading state for asynchronous API operations
 * - Handle and display error messages to the user
 * 
 * Key Features:
 * - Dynamic policy tab generation based on subscription data
 * - Real-time subscription filtering without page reload
 * - Modal-based device details display
 * - Automatic loading state management
 * - Comprehensive error handling with user-friendly messages
 * 
 * @selector app-subscription-plans
 * @template ./subscription-plans.component.html
 * @styleUrl ./subscription-plans.component.css
 * @implements {OnInit}
 */
@Component({
  selector: 'app-subscription-plans',
  templateUrl: './subscription-plans.component.html',
  styleUrls: ['./subscription-plans.component.css']
})
export class SubscriptionPlansComponent implements OnInit {

  /**
   * @property {CreditsManagersModel[]} subscriptions
   * @description Full list of all subscriptions fetched from the backend API.
   * Contains the complete dataset that is used for filtering and display operations.
   * Initially populated in ngOnInit and used as the source for filtered results.
   */
  public subscriptions: CreditsManagersModel[] = [];

  /**
   * @property {boolean} isLoading
   * @description Loading state flag for API operations. True during data fetching,
   * false when idle. Used to display/hide loading spinners and disable UI elements
   * during asynchronous operations.
   */
  public isLoading: boolean = false;

  /**
   * @type {SubscriberPolicyDetails[]} policies
   * @description Array of unique policy details extracted from all subscriptions.
   * Derived from the subscriptions data and used to populate policy filter tabs.
   * Contains one entry per unique policy flag found in subscriptions.
   */
  public policies: SubscriberPolicyDetails[] = [];

  /**
   * @property {CreditsManagersModel[]} filteredSubscriptions
   * @description Working copy of subscriptions filtered by selected policy.
   * Initially contains all subscriptions, updated whenever user selects a policy tab.
   * This is the data source displayed in the subscription list UI.
   */
  public filteredSubscriptions: CreditsManagersModel[] = [];

  /**
   * @type {any[]} deviceDetails
   * @description Array of device details for the currently selected subscription.
   * Populated when user clicks to view device details and displayed in a modal popup.
   * Cleared when modal is closed to prevent data leakage between selections.
   */
  public deviceDetails: any[] = [];

  /**
   * @method constructor
   * @description Initializes the component with required Angular and application services
   * for subscription management and user notifications.
   * 
   * @param {ToastrService} toastrService - NGX-Toastr service for displaying toast notifications
   * to the user (errors, warnings, success messages)
   * @param {CreditsManagersService} creditsManagerService - Service for fetching subscription data
   * from the backend API
   */
  constructor(
    private toastrService: ToastrService,
    private creditsManagerService: CreditsManagersService
  ) {}

  /**
   * @method ngOnInit
   * @description Angular lifecycle hook called when the component is initialized.
   * Performs the following operations:
   * 1. Sets loading state to true
   * 2. Fetches all subscriptions for the logged-in business from the backend
   * 3. Creates a filtered copy for use in tab filtering
   * 4. Extracts unique policy details from subscriptions for tab generation
   * 5. Sets loading state to false and displays any errors via toast notifications
   * 
   * If subscription fetch fails, displays error message but component remains functional.
   * Policy extraction happens even if no subscriptions are returned.
   * 
   * @async
   * @returns {Promise<void>}
   */
  public async ngOnInit(): Promise<void> {
    try {
      this.isLoading = true;

      try {
        this.subscriptions = await this.creditsManagerService.getSubscriptionsByBusinessID();
      } catch (error) {
        this.toastrService.error(error.error?.message ?? 'Failed to fetch subscriptions');
      }

      // Keep a reference copy for filtering
      this.filteredSubscriptions = this.subscriptions;

      // Extract unique policy details
      this.extractUniquePolicies();

      this.isLoading = false;
    } catch (error) {
      this.toastrService.error(error.error?.message || error);
      this.isLoading = false;
    }
  }

  /**
   * @method extractUniquePolicies
   * @description Extracts unique policy details from the subscriptions array based on policy flags.
   * Uses a JavaScript Map to deduplicate policies by their flag, ensuring each unique policy
   * appears only once in the policies array. Result is used to generate policy filter tabs.
   * 
   * Implementation details:
   * - Maps each subscription to [policyFlag, policyDetails] tuple
   * - Uses Map to store unique entries (duplicate flags overwrite)
   * - Extracts values from map to create the final policies array
   * 
   * @private
   * @returns {void}
   */
  private extractUniquePolicies(): void {
    this.policies = Array.from(
      new Map(
        this.subscriptions.map(s => [s.policyDetails.policyFlag, s.policyDetails])
      ).values()
    );
  }

  /**
   * @method filterByPolicy
   * @description Filters the subscriptions list based on the selected policy flag.
   * If 'ALL' is selected, displays all subscriptions. Otherwise, filters subscriptions
   * to show only those matching the specified policy flag.
   * 
   * Updates the filteredSubscriptions property which is bound to the UI, causing
   * the displayed list to update in real-time without page reload.
   * 
   * @param {string} policyFlag - Policy flag to filter by, or 'ALL' to show all subscriptions
   * @returns {void}
   * 
   * @example
   * // Show all subscriptions
   * this.filterByPolicy('ALL');
   * 
   * // Show only subscriptions with policy flag 'PREMIUM'
   * this.filterByPolicy('PREMIUM');
   */
  public filterByPolicy(policyFlag: string): void {
    if (policyFlag === 'ALL') {
      this.filteredSubscriptions = this.subscriptions;
    } else {
      this.filteredSubscriptions = this.subscriptions.filter(
        s => s.policyDetails?.policyFlag === policyFlag
      );
    }
  }

  /**
   * @method showDeviceDetails
   * @description Displays device details for a selected subscription in a modal popup.
   * Finds the subscription by ID, extracts its device details array, and opens the
   * modal dialog with the information. Modal is non-dismissible (backdrop: static).
   * 
   * If subscription is not found or has no device details, displays empty modal.
   * Uses jQuery Bootstrap modal plugin for UI display.
   * 
   * @param {string} subscriberID - The unique subscription ID to fetch device details for
   * @returns {void}
   * 
   * @example
   * // Called from template when user clicks a subscription row
   * this.showDeviceDetails('sub-12345');
   */
  public showDeviceDetails(subscriberID: string): void {
    const subscription = this.subscriptions.find(s => s.id === subscriberID);
    this.deviceDetails = subscription?.devicesDetails || [];

    $("#deviceDetailsPopup").modal({
      backdrop: 'static',
      keyboard: false,
      show: true
    });
  }

  /**
   * @method closeDeviceDetailsModal
   * @description Hides the device details modal popup and clears the device details data.
   * Should be called when user clicks close/cancel button on the modal.
   * Clears deviceDetails array to prevent data from previous selections appearing
   * when modal is opened again.
   * 
   * Uses jQuery Bootstrap modal plugin to hide the modal without destroying it,
   * allowing it to be reopened with new data.
   * 
   * @returns {void}
   */
  public closeDeviceDetailsModal(): void {
    $("#deviceDetailsPopup").modal('hide');
    this.deviceDetails = [];
  }
}
